import React, { useEffect, useMemo, useState } from 'react';
import Tooltip from '../components/tooltip';
import Acordion from '../components/Acordion';
import AcordionItem from '../components/AcordionItem'; 
import Modal from '../components/Modal';
import ResumenCalculo from '../components/ResumenCalculo';
import Loader from '../components/Loader'; 

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5257';

const CalculoMuellaje = ({ onCalculoTerminado }) => {
  const [puertos, setPuertos] = useState([]);
  const [puertoId, setPuertoId] = useState('');

  const [serviciosRaw, setServiciosRaw] = useState([]);
  const [servicioId, setServicioId] = useState('');

  const [tipo_trafico, setTipoTrafico] = useState('altura');
  const [cantidad, setCantidad] = useState('');

  const [cargando, setCargando] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const safeLower = (v) => (typeof v === 'string' ? v.toLowerCase() : '');
  const etiquetaServicio = (s) => {
    const nombre = s.nombre || s.servicioNombre || s.servicio || 'Servicio';
    const tipo = s.tipo || '';
    const trafico = s.tipo_de_trafico || s.tipoDeTrafico || '';
    const base = [nombre, tipo].filter(Boolean).join(' · ');
    return trafico ? `${base} (${trafico})` : base;
  };

  useEffect(() => {
    (async () => {
      try {
        setCargando(true); setModalData(null);
        const r = await fetch(`${API_BASE_URL}/api/Puerto`);
        const data = r.ok ? await r.json() : [];
        const norm = (data || [])
          .map(p => ({
            ...p,
            value: p.id || p._id || p.codigo,
            label: p.nombre || p.displayName
          }))
          .filter(p => !!p.value && !!p.label)
          .sort((a,b)=> (a.label||'').localeCompare(b.label||''));
        setPuertos(norm);
      } catch {
        setPuertos([]);
        setModalData({ error: 'Error al cargar puertos.' });
        setIsModalOpen(true);
      } finally { setCargando(false); }
    })();
  }, []);

  const puertoSel = useMemo(
    () => puertos.find(p => String(p.value) === String(puertoId)),
    [puertos, puertoId]
  );
  const esVeracruz = useMemo(
    () => /veracruz/i.test(puertoSel?.label || ''),
    [puertoSel]
  );

  useEffect(() => {
    setServiciosRaw([]); setServicioId('');
    if (!puertoId) return;

    (async () => {
      try {
        setCargando(true); setModalData(null);
        const r = await fetch(`${API_BASE_URL}/api/Servicio/por-puerto/${encodeURIComponent(puertoId)}`);
        const list = r.ok ? await r.json() : [];

        const soloMuellaje = (list || []).filter(s =>
          safeLower(s.servicioNombre || s.servicio || '').includes('muella')
        );

        const arr = (soloMuellaje || [])
          .map(s => ({
            ...s,
            _idNorm: s.id, 
            _label: etiquetaServicio(s)
          }))
          .filter(s => !!s._idNorm);

        setServiciosRaw(arr);
      } catch {
        setServiciosRaw([]);
        setModalData({ error: 'Error al cargar servicios.' });
        setIsModalOpen(true);
      } finally { setCargando(false); }
    })();
  }, [puertoId]);

  const servicios = useMemo(() => {
    if (!esVeracruz) return serviciosRaw;
    const t = safeLower(tipo_trafico);
    return serviciosRaw.filter(s => {
      const ts = safeLower(s.tipo_de_trafico || s.tipoDeTrafico || '');
      if (!ts) return false;
      return ts.includes(t);
    });
  }, [serviciosRaw, tipo_trafico, esVeracruz]);

  useEffect(() => {
    if (!servicios.length) {
      setServicioId('');
    } else if (!servicios.some(s => String(s._idNorm) === String(servicioId))) {
      setServicioId(String(servicios[0]?._idNorm || ''));
    }
  }, [servicios, servicioId]);

  const servicioSeleccionado = useMemo(() => {
    if (!servicioId) return null;
    return servicios.find(s => String(s._idNorm) === String(servicioId));
  }, [servicios, servicioId]);

  const valido = useMemo(() => {
    if (!puertoId) return false;
    if (!servicioId) return false;
    const n = Number.parseInt(cantidad, 10);
    if (Number.isNaN(n) || n <= 0) return false;
    return true;
  }, [puertoId, servicioId, cantidad]);

  const onCalcular = async (e) => {
    e.preventDefault();
    setModalData(null); setIsModalOpen(false);
    if (!valido) return;
    
    const finalPayload = {
      puertoId: puertoId,
      servicioId: servicioId,
      cantidad: Number.parseInt(cantidad, 10),
      tipo_trafico: esVeracruz ? tipo_trafico : null
    };

    try {
      setCargando(true);
      const r = await fetch(`${API_BASE_URL}/api/calculos/muellaje`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalPayload)
      });
      if (!r.ok) {
        let msg = `Error ${r.status}`;
        try { const t = await r.json(); if (t?.error) msg = t.error; } catch {}
        throw new Error(msg);
      }
      
      const data = await r.json();

      if (onCalculoTerminado && typeof onCalculoTerminado === 'function') {
        onCalculoTerminado(data.totalCobro);
      }
      
      const nombreServicio = servicioSeleccionado?._label || (servicioSeleccionado ? etiquetaServicio(servicioSeleccionado) : 'Muellaje');

      const modalPayload = {
        total: data.totalCobro,
        desglose: data.detalle,
        servicio: nombreServicio, 
        criterio: `Cantidad: ${finalPayload.cantidad} (${data.moneda || 'MXN'})`
      };

      setModalData(modalPayload);
      setIsModalOpen(true);

    } catch (e) {
      setModalData({ error: e.message || 'Error al calcular.' });
      setIsModalOpen(true);
    } finally {
      setCargando(false);
    }
  };

  return (
    <>
      <Acordion defaultOpenIndex={0}>
        <AcordionItem title="Muellaje">

          {cargando ? (
            <Loader message="Procesando solicitud..." />
          ) : (
            <div style={{ maxWidth:'600px', margin:'0 auto' }}>
              <label>Puerto</label>
              <select
                value={puertoId}
                onChange={e => setPuertoId(String(e.target.value))}
                style={{ width:'100%', marginBottom:'.75rem' }}
                disabled={cargando}
                className="form-control" 
              >
                <option value="" disabled>Selecciona un puerto</option>
                {puertos.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>

              <div style={{ display:'flex', alignItems:'center', marginBottom:'.25rem' }}>
                <label style={{ flex:1 }}>Selecciona servicio de muellaje</label>
                <Tooltip text={esVeracruz
                  ? "Tarifa por el uso de la infraestructura del muelle para cargar, descargar o transferir mercancías."
                  : "Tarifa por el uso de la infraestructura del muelle para cargar, descargar o transferir mercancías."} />
              </div>
              <select
                value={servicioId}
                onChange={e => setServicioId(e.target.value)}
                style={{ width:'100%', marginBottom:'.75rem' }}
                disabled={!puertoId || servicios.length === 0 || cargando}
                className="form-control" 
              >
                <option value="" disabled>
                  {puertoId ? 'Selecciona servicio de muellaje' : 'Primero el puerto'}
                </option>
                {servicios.map(s => (
                  <option key={s._idNorm} value={s._idNorm}>
                    {s._label}
                  </option>
                ))}
              </select>

              {puertoId && servicios.length === 0 && (
                <p style={{color:'#b00020', marginTop:'-0.25rem', marginBottom:'.5rem'}}>
                  No hay servicios de muellaje para la selección actual.
                </p>
              )}

              {esVeracruz && (
                <>
                  <div style={{ display:'flex', alignItems:'center', marginBottom:'.25rem' }}>
                    <label style={{ flex:1 }}>Tipo de tráfico</label>
                    <Tooltip text="Altura: internacional. Cabotaje: entre puertos del mismo país." />
                  </div>
                  <select
                    value={tipo_trafico}
                    onChange={e => setTipoTrafico(e.target.value)}
                    style={{ width:'100%', marginBottom:'.75rem' }}
                    disabled={cargando}
                    className="form-control" 
                  >
                    <option value="altura">Altura</option>
                    <option value="cabotaje">Cabotaje</option>
                  </select>
                </>
              )}

              <label>
                {servicioSeleccionado?.unidadCobro || 
                 servicioSeleccionado?.unidad_cobro || 
                 servicioSeleccionado?.UnidadCobro || 
                 'Unidades'}
              </label>

              <input
                type="number" min="1" step="1"
                value={cantidad}
                onChange={e => setCantidad(e.target.value)}
                placeholder="Ej. 100"
                style={{ width:'100%', marginBottom:'.75rem' }}
                disabled={cargando}
                className="form-control" 
              />

              <button
                onClick={onCalcular}
                style={{ padding:'0.5rem 1rem', cursor:'pointer' }}
                disabled={cargando || !valido}
                className="form-control" 
              >
                {cargando ? 'Calculando…' : 'Calcular'}
              </button>
            </div>
          )}
        </AcordionItem>
      </Acordion>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {modalData && (
          modalData.error ? (
            <div>
              <h2 style={{ marginTop: 0, color: '#D32F2F' }}>Error en el Cálculo</h2>
              <p>{modalData.error}</p>
            </div>
          ) : (
            <ResumenCalculo data={modalData} />
          )
        )}
      </Modal>
    </>
  );
};

export default CalculoMuellaje;
